// Copyright 1999-2024. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    BackupNodeType,
    IS3Credentials,
    IBackupNodeCreateRequest,
    IBackupNodeResponse,
    IHetznerStorageBoxCredentials,
    ISSHRsyncCredentials,
} from 'common/api/resources/BackupNode';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as backupNodesActions from 'admin/backupNode/actions';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import {
    Column,
    Columns,
    Form,
    FormField,
    FormFieldText,
    Section,
    setIn,
    Translate,
} from '@plesk/ui-library';
import { Button } from 'admin/common/components/Button/Button';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    FormErrorsState,
    IFormErrors,
} from 'common/modules/app/formErrors/reducer';
import AsyncSelectInput from 'common/components/Select/AsyncSelectInput';
import { createOptionsLoader } from 'common/components/Select/helpers';
import { connect } from 'react-redux';
import { ISelectOption } from 'common/components';
import {
    intMinRule,
    IValidationRules,
    numericRule,
    requiredRule,
    urlRule,
    validate,
} from 'common/validator';
import {
    computeResources,
    IComputeResourceResponse,
} from 'common/api/resources/ComputeResource';
import { SegmentedControl } from 'common/components/SegmentedControl/SegmentedControl';
import { humanizeType } from 'admin/backupNode/constants';
import { nestStringProperties } from 'common/modules/app/formErrors/selectors';
import FormErrors from 'common/components/Form/FormErrors/FormErrors';
import { FORM } from 'admin/backupNode/constants/tests';
import { FormFieldNumber } from 'common/components/Form/FormFieldNumber/FormFieldNumber';

interface IBackupNodeFormProps {
    onSubmit: () => void;
}

export type BackupNodeFormProps =
    IBackupNodeFormProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const typeOptions = Object.values(BackupNodeType).map((type) => ({
    value: type.toString(),
    title: humanizeType(type),
}));

const convertNodeToRequest = (node: IBackupNodeResponse): IBackupNodeCreateRequest => ({
    name: node.name,
    type: node.type,
    credentials: { ...node.credentials },
    compute_resources: node.compute_resources ? node.compute_resources.map((cr) => cr.id) : [],
    settings: node.settings,
});

const computeResourceToOption = (cr: IComputeResourceResponse) => ({
    label: cr.name,
    value: cr.id.toString(),
});

const loadComputeResourceOptions = createOptionsLoader(
    computeResources.list,
    computeResourceToOption
);

const renderNotAccessibleErrors = (formErrors: FormErrorsState) => {
    const errors: IFormErrors = formErrors;

    if (!errors?.notAccessible) {
        return null;
    }

    return (
        <FormErrors
            title={<Translate content="backupNode.nodeNotAccessible"/>}
            errors={errors.notAccessible}
        />
    );
};

export const BackupNodeForm: React.FC<BackupNodeFormProps> = ({
    item,
    formErrors,
    isCreating,
    onSubmit,
    backupNodesActions: {
        createBackupNode,
        updateBackupNode,
    },
    formErrorsActions: {
        setFormErrors,
    },
}) => {
    const [ submitValues, setSubmitValues ] = React.useState(convertNodeToRequest(item));
    const [ selectedCRs, setSelectedCRs ] = React.useState<ISelectOption[]>(item.compute_resources.map(computeResourceToOption));
    const isUpdating = !!item.id;

    const handleFieldChange = (field: string, value: string) => setSubmitValues(setIn(submitValues, field, value));

    const renderCredentials = (type: BackupNodeType) => {
        switch (type) {
        case BackupNodeType.SSH_RSYNC:
            return (
                <>
                    <FormFieldText
                        name="credentials[host]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.host" />}
                        description={<Translate content="backupNode.form.hostDescription" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[port]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.port" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[login]"
                        size="fill"
                        label={<Translate content="backupNode.form.login" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[key]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.key" />}
                        required={!isUpdating}
                        multiline={true}
                    />
                    <FormFieldText
                        name="credentials[storage_path]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.storagePath" />}
                        description={<Translate content="backupNode.form.sshStoragePathDescription" />}
                        required={true}
                    />
                </>
            );
        case BackupNodeType.HETZNER_STORAGE_BOX:
            return (
                <>
                    <FormFieldText
                        name="credentials[host]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.host" />}
                        description={<Translate content="backupNode.form.hostDescription" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[login]"
                        size="fill"
                        label={<Translate content="backupNode.form.login" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[key]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.key" />}
                        required={!isUpdating}
                        multiline={true}
                    />
                </>
            );
        case BackupNodeType.S3:
            return (
                <>
                    <FormFieldText
                        name="credentials[access_key_id]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.accessKeyId" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[secret_access_key]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.secretAccessKey" />}
                        required={!isUpdating}
                    />
                    <FormFieldText
                        name="credentials[region]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.region" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[bucket]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.bucket" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[storage_path]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.storagePath" />}
                        description={<Translate content="backupNode.form.s3StoragePathDescription" />}
                        required={true}
                    />
                    <FormFieldText
                        name="credentials[endpoint]"
                        size={SIZE.FILL}
                        label={<Translate content="backupNode.form.endpoint" />}
                        description={<Translate content="backupNode.form.endpointDescription" />}
                        placeholder="https://s3.eu-central-1.example.com"
                    />
                </>
            );
        }

        return null;
    };

    const handleSubmit = async (data: IBackupNodeCreateRequest) => {
        let rules: IValidationRules = {
            name: requiredRule(<Translate content="validate.fieldRequired" />),
            type: requiredRule(<Translate content="validate.fieldRequired" />),
            'settings.concurrent_backups.create': intMinRule(<Translate content="validate.fieldRequired" params={{ min: 1 }}/>, 1),
            'settings.concurrent_backups.restore': intMinRule(<Translate content="validate.fieldRequired" params={{ min: 1 }}/>, 1),
        };

        switch (data.type) {
        case BackupNodeType.SSH_RSYNC:
            data.credentials = {
                host: (data.credentials as ISSHRsyncCredentials).host,
                port: (data.credentials as ISSHRsyncCredentials).port,
                login: (data.credentials as ISSHRsyncCredentials).login,
                key: (data.credentials as ISSHRsyncCredentials).key,
                storage_path: (data.credentials as ISSHRsyncCredentials).storage_path,
            };
            rules = {
                ...rules,
                'credentials.host': requiredRule(<Translate content="validate.fieldRequired"/>),
                'credentials.port': numericRule(<Translate content="validate.fieldNumeric"/>),
                'credentials.login': requiredRule(<Translate content="validate.fieldRequired"/>),
                'credentials.storage_path': requiredRule(<Translate content="validate.fieldRequired"/>),
            };

            if (!isUpdating) {
                rules['credentials.key'] = requiredRule(<Translate content="validate.fieldRequired" />);
            }
            break;

        case BackupNodeType.HETZNER_STORAGE_BOX:
            data.credentials = {
                host: (data.credentials as IHetznerStorageBoxCredentials).host,
                login: (data.credentials as IHetznerStorageBoxCredentials).login,
                key: (data.credentials as IHetznerStorageBoxCredentials).key,
            };
            rules = {
                ...rules,
                'credentials.host': requiredRule(<Translate content="validate.fieldRequired"/>),
                'credentials.login': requiredRule(<Translate content="validate.fieldRequired"/>),
            };

            if (!isUpdating) {
                rules['credentials.key'] = requiredRule(<Translate content="validate.fieldRequired" />);
            }
            break;

        case BackupNodeType.S3:
            data.credentials = {
                access_key_id: (data.credentials as IS3Credentials).access_key_id,
                secret_access_key: (data.credentials as IS3Credentials).secret_access_key,
                region: (data.credentials as IS3Credentials).region,
                bucket: (data.credentials as IS3Credentials).bucket,
                storage_path: (data.credentials as IS3Credentials).storage_path,
                endpoint: (data.credentials as IS3Credentials).endpoint?.trim(),
            };
            rules = {
                ...rules,
                'credentials.access_key_id': requiredRule(<Translate content="validate.fieldRequired"/>),
                'credentials.region': requiredRule(<Translate content="validate.fieldRequired"/>),
                'credentials.bucket': requiredRule(<Translate content="validate.fieldRequired"/>),
                'credentials.storage_path': requiredRule(<Translate content="validate.fieldRequired"/>),
            };

            if (data.credentials.endpoint) {
                rules['credentials.endpoint'] = urlRule(<Translate content="validate.badUrl" />);
            }

            if (!isUpdating) {
                rules['credentials.secret_access_key'] = requiredRule(<Translate content="validate.fieldRequired" />);
            }
            break;
        }

        const errors = validate<IBackupNodeCreateRequest>(data, rules);

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        if (isUpdating) {
            await updateBackupNode(item.id, data);
        } else {
            await createBackupNode(data);
        }
        onSubmit();
    };

    const handleCRsChange = (crs: ISelectOption[]) => {
        if (!crs) {
            crs = [];
        }
        setSelectedCRs(crs);
        setSubmitValues((state) => ({
            ...state,
            compute_resources: crs.map((opt) => opt.value as number),
        }));
    };

    return (
        <>
            <Form
                id="backupNodeForm"
                footerClassName="hidden"
                onSubmit={handleSubmit}
                onFieldChange={handleFieldChange}
                values={submitValues}
                errors={formErrors}
                hideRequiredLegend={true}
                submitButton={false}
                cancelButton={false}
                applyButton={false}
                vertical={true}
            >
                <Section>
                    <FormFieldText
                        name="name"
                        size="fill"
                        label={<Translate content="backupNode.form.name" />}
                        required={true}
                    />
                    <FormField
                        name="type"
                        required={true}
                        value={submitValues.type}
                        label={<Translate content="backupNode.form.type" />}
                    >
                        {({ setValue }) => (
                            <SegmentedControl
                                buttons={typeOptions}
                                selected={submitValues.type}
                                onChange={setValue}
                                data-cy={FORM.TYPE_SELECTOR}
                            />
                        )}
                    </FormField>
                    <FormField
                        label={<Translate content="backupNode.form.concurrentBackups.title" />}
                        required={true}
                    >
                        <Columns gap={SIZE.MD} vertical={false}>
                            <Column width={219}>
                                <FormFieldNumber
                                    name="settings[concurrent_backups[create]]"
                                    label={<Translate content="backupNode.form.concurrentBackups.create" />}
                                    size={SIZE.FILL}
                                    min={1}
                                    vertical
                                />
                            </Column>
                            <Column width={219}>
                                <FormFieldNumber
                                    name="settings[concurrent_backups[restore]]"
                                    label={<Translate content="backupNode.form.concurrentBackups.restore" />}
                                    size={SIZE.FILL}
                                    min={1}
                                    vertical
                                />
                            </Column>
                        </Columns>
                    </FormField>
                    <FormField
                        name="compute_resources"
                        label={<Translate content="backupNode.form.computeResources" />}
                    >
                        <AsyncSelectInput
                            value={selectedCRs}
                            loadOptions={loadComputeResourceOptions}
                            onChange={handleCRsChange}
                            debounceTimeout={1000}
                            additional={{ page: 1 }}
                            menuPosition="fixed"
                            isMulti={true}
                        />
                    </FormField>
                    {renderCredentials(submitValues.type)}
                </Section>
            </Form>
            {
                renderNotAccessibleErrors(formErrors)
            }
            <Button
                type="submit"
                form="backupNodeForm"
                fill={true}
                intent={INTENT_TYPE.PRIMARY}
                size={SIZE.LG}
                isLoading={isCreating}
            >
                <Translate content="backupNode.saveBtn" />
            </Button>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    item: state.backupNode.item,
    formErrors: nestStringProperties(state),
    isCreating: state.app.loadingFlags.has(LOADING_FLAGS.BACKUP_NODE_SAVE),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    backupNodesActions: bindActionCreators(backupNodesActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(BackupNodeForm);
